<template>
    <v-card flat color="transparent">
        <v-card-title class="d-block">
            <v-btn class="mb-3" small text color="secondary" :to="{ name: 'reporting-rp-overview', params: { rpid: $route.params.rpid } }">
                <v-icon left>mdi-arrow-left</v-icon>
                Resources dashboard
            </v-btn>
            <div>
                {{ title }}
            </div>
        </v-card-title>
        <v-card-subtitle>{{ status.long_id }}</v-card-subtitle>
        <v-card-text>
            <v-skeleton-loader v-if="!allLoaded" class="mx-auto" type="table"></v-skeleton-loader>
            <template v-else>
                <div class="d-flex align-center">
                    <div>
                        <limit-dialog
                            :rpid="Number($route.params.rpid)"
                            @success="fetch"
                            :limitType="limitType"
                            :prefill="{ start_date: this.status.resource_pool_start, end_date: this.status.resource_pool_end }">
                            <v-btn color="primary" class="mr-2">
                                <v-icon left>add</v-icon>
                                {{ createBtnLabel }}
                            </v-btn>
                        </limit-dialog>
                    </div>
                    <v-spacer></v-spacer>
                    <v-text-field
                        prepend-inner-icon="mdi-magnify "
                        autofocus
                        hide-details
                        solo
                        flat
                        dense
                        :placeholder="searchLabel"
                        v-model="search"
                        clearable
                        class="mr-2"></v-text-field>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn icon v-on="on" :loading="fetching" @click="fetch">
                                <v-icon>refresh</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ refreshLabel }}</span>
                    </v-tooltip>
                </div>
                <v-data-table
                    class="mt-4"
                    :search="search"
                    :headers="headers"
                    :options="tableOptions"
                    :items="history"
                    :footer-props="{ 'items-per-page-options': itemsPerPageOptions }">
                    <template v-slot:top="{ pagination, options, updateOptions }">
                        <v-data-footer
                            :pagination="pagination"
                            :options="options"
                            @update:options="updateOptions"
                            :itemsPerPageOptions="itemsPerPageOptions"
                            items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                            class="no-border" />
                    </template>
                    <template v-slot:no-data>
                        <span>No history found.</span>
                    </template>
                    <template v-slot:[`item.limit_bytes`]="{ item }">
                        <span>{{ humanFileSize(item.limit_bytes) }}</span>
                    </template>
                    <template v-slot:[`item.ncu_usage`]="{ item }">
                        <v-tooltip top>
                            <template v-slot:activator="{ on }">
                                <v-progress-linear
                                    v-on="on"
                                    :value="item.total_ncu_hours_used_pct * 100"
                                    rounded
                                    :color="usageProgressColor(item.total_ncu_hours_used_pct * 100)"
                                    class="my-2"
                                    height="8"></v-progress-linear>
                            </template>
                            <span>{{ Math.floor(item.total_ncu_hours_used).toLocaleString() }} NCU hours</span>
                        </v-tooltip>
                        <div class="text--secondary text-right">
                            <strong>{{ Math.floor(item.total_ncu_hours_used_pct * 100).toLocaleString() }}% used</strong>
                        </div>
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                        <v-menu left offset-x nudge-top="10">
                            <template v-slot:activator="{ on }">
                                <v-btn v-on="on" text color="secondary" small>
                                    <v-icon>more_horiz</v-icon>
                                </v-btn>
                            </template>
                            <v-list nav dense>
                                <v-list-item>
                                    <limit-dialog crud="update" :prefill="item" :limitType="limitType" :rpid="Number($route.params.rpid)" @success="fetch">
                                        <v-btn block text color="secondary" class="justify-start">
                                            <v-icon left>mdi-pencil-outline</v-icon>
                                            Edit
                                        </v-btn>
                                    </limit-dialog>
                                </v-list-item>
                                <v-list-item>
                                    <confirm-delete-dialog :limit="item" @success="fetch" :limitType="limitType">
                                        <v-btn block text color="error" class="justify-start">
                                            <v-icon left>mdi-trash-can-outline</v-icon>
                                            Delete
                                        </v-btn>
                                    </confirm-delete-dialog>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </template>
                </v-data-table>
            </template>
        </v-card-text>
    </v-card>
</template>

<script>
import { humanFileSize } from '@/utils'

const LimitDialog = () => import('@/modules/user/components/LimitDialog.vue')
const ConfirmDeleteDialog = () => import('@/modules/user/components/ConfirmDeleteDialog.vue')

export default {
    name: 'LimitHistory',
    components: { LimitDialog, ConfirmDeleteDialog },
    props: {
        limitType: { type: String, default: 'ncu' } // 'ncu', 'fs', 'resting'
    },
    data() {
        return {
            search: '',
            name: '',
            history: [],
            itemsPerPageOptions: [25, 50, 100, -1],
            tableOptions: {
                page: 0,
                itemsPerPage: 25,
                pageStart: 1,
                pageStop: 25,
                itemsLength: 25
            },
            fetching: false,
            fetchingStatus: false,
            status: null
        }
    },
    async created() {
        this.getStatus()
        switch (this.limitType) {
            case 'ncu':
                this.fetchNcuLimitHistory()
                break
            case 'fs':
                this.fetchFsLimitHistory()
                break
            case 'resting':
                this.fetchRestingLimitHistory()
                break
        }
    },
    methods: {
        usageProgressColor(usagePercent) {
            return usagePercent < 60 ? 'success' : usagePercent < 90 ? 'amber' : 'error'
        },
        async fetchNcuLimitHistory() {
            try {
                this.fetching = true
                const { data } = await this.$axios.get(`/resource/${this.$route.params.rpid}/ncul`)
                this.history = data.ncu_limits || []
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Failed to fetch NCU limit history.',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'error'
                })
            }
            this.fetching = false
        },
        async fetchFsLimitHistory() {
            try {
                this.fetching = true
                const { data } = await this.$axios.get(`/resource/${this.$route.params.rpid}/fsl`)
                this.history = data.storage_limits || []
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Failed to fetch File system limit history.',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'error'
                })
            }
            this.fetching = false
        },
        async fetchRestingLimitHistory() {
            try {
                this.fetching = true
                const { data } = await this.$axios.get(`/resource/${this.$route.params.rpid}/rsl`)
                this.history = data.storage_limits || []
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Failed to fetch Resting limit history.',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'error'
                })
            }
            this.fetching = false
        },
        humanFileSize(bytes, si) {
            return humanFileSize(bytes, si, 2)
        },
        async getStatus() {
            try {
                this.fetchingStatus = true
                const { data } = await this.$axios.get(`/resource/${this.$route.params.rpid}/status`)
                this.status = data
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Could not fetch utilization data.',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'error'
                })
            } finally {
                this.fetchingStatus = false
            }
        }
    },
    computed: {
        title() {
            switch (this.limitType) {
                case 'ncu':
                    return 'NCU limit history'
                case 'fs':
                    return 'File system limit history'
                case 'resting':
                    return 'Resting storage limit history'
                default:
                    return 'Limit history'
            }
        },
        searchLabel() {
            switch (this.limitType) {
                case 'ncu':
                    return 'Search for a NCU limit...'
                case 'fs':
                    return 'Search for a File system limit...'
                case 'resting':
                    return 'Search for a Resting storage limit...'
                default:
                    return 'Search for a limit...'
            }
        },
        refreshLabel() {
            switch (this.limitType) {
                case 'ncu':
                    return 'Refresh NCU limit history'
                case 'fs':
                    return 'Refresh File system limit history'
                case 'resting':
                    return 'Refresh Resting storage limit history'
                default:
                    return 'Refresh limit history'
            }
        },
        createBtnLabel() {
            switch (this.limitType) {
                case 'ncu':
                    return 'NCU limit'
                case 'fs':
                    return 'File system limit'
                case 'resting':
                    return 'Resting storage limit'
                default:
                    return 'Limit'
            }
        },
        fetch() {
            switch (this.limitType) {
                case 'ncu':
                    return this.fetchNcuLimitHistory
                case 'fs':
                    return this.fetchFsLimitHistory
                case 'resting':
                    return this.fetchRestingLimitHistory
                default:
                    return this.fetchNcuLimitHistory
            }
        },
        headers() {
            const headers = [
                { text: 'Start date', value: 'start_date' },
                { text: 'End date', value: 'end_date' }
            ]
            switch (this.limitType) {
                case 'ncu':
                    headers.push({ text: 'NCU limit', value: 'limit_ncuhour' }, { text: 'NCU usage', value: 'ncu_usage' })
                    break
                case 'fs':
                    headers.push({ text: 'File system limit', value: 'limit_bytes' })
                    break
                case 'resting':
                    headers.push({ text: 'Resting storage limit', value: 'limit_bytes' })
                    break
            }
            headers.push({ text: 'Actions', value: 'actions', align: 'right', sortable: false })
            return headers
        },
        allLoaded() {
            return !this.fetching && !this.fetchingStatus
        }
    },
    watch: {}
}
</script>
